import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Cart drawer product recommendations: carousel shell aligned with `sections/product-recommendations.liquid`.
 * Each slide is the theme `_product-card` markup from the Section Rendering API
 * (`sections/mini-cart-recommendation.liquid` at `/products/...?section_id=...`).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} carouselMount
 * @property {HTMLElement} [status]
 * @property {HTMLTemplateElement} [slideshowArrowsTpl]
 * @property {HTMLScriptElement} [catalogFallback]
 *
 * @typedef {{ id: number; handle?: string; title?: string; url?: string }} RecommendationProduct
 *
 * @extends {Component<Refs>}
 */
class CartProductRecommendations extends Component {
  /** @type {AbortController | null} */
  #documentAbort = null;

  /** @type {AbortController | null} */
  #fetchAbort = null;

  /** @type {Record<string, RecommendationProduct[]>} */
  #recommendationsCache = {};

  /** @type {Map<string, string>} */
  #cardMarkupCache = new Map();

  /** @type {ReturnType<typeof setTimeout> | null} */
  #postMorphRefreshTimer = null;

  /** @type {number} */
  #transientRefreshRetryCount = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#documentAbort = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#documentAbort.signal,
    });
    this.#scheduleRefresh();
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.isConnected) return;
    this.#scheduleRefresh(64);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#postMorphRefreshTimer != null) {
      clearTimeout(this.#postMorphRefreshTimer);
      this.#postMorphRefreshTimer = null;
    }
    this.#documentAbort?.abort();
    this.#fetchAbort?.abort();
  }

  #onCartUpdate = () => {
    this.#scheduleRefresh(48);
  };

  /**
   * @param {number} [delay=0]
   */
  #scheduleRefresh(delay = 0) {
    if (!this.isConnected) return;
    if (this.#postMorphRefreshTimer != null) clearTimeout(this.#postMorphRefreshTimer);
    this.#postMorphRefreshTimer = setTimeout(() => {
      this.#postMorphRefreshTimer = null;
      this.#refresh();
    }, delay);
  }

  /**
   * @returns {boolean}
   */
  #scheduleTransientRetry() {
    if (this.#transientRefreshRetryCount >= 1) return false;
    this.#transientRefreshRetryCount += 1;
    this.#scheduleRefresh(180);
    return true;
  }

  /**
   * @param {number} [maxFrames=32]
   * @returns {Promise<HTMLElement | null>}
   */
  async #waitForCarouselMount(maxFrames = 32) {
    for (let i = 0; i < maxFrames; i++) {
      const el = this.#mountEl();
      if (el) return el;
      await new Promise((r) => requestAnimationFrame(r));
    }
    return this.#mountEl();
  }

  /**
   * @returns {HTMLElement | null}
   */
  #mountEl() {
    const fromRefs = this.refs.carouselMount;
    if (fromRefs instanceof HTMLElement) return fromRefs;
    const found = this.querySelector('[ref="carouselMount"]');
    return found instanceof HTMLElement ? found : null;
  }

  async #refresh() {
    const mountReady = await this.#waitForCarouselMount();
    if (!mountReady) return;

    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();
    const { signal } = this.#fetchAbort;

    this.setAttribute('aria-busy', 'true');

    try {
      const cartBase =
        typeof Theme !== 'undefined' && Theme.routes?.cart_url
          ? String(Theme.routes.cart_url).replace(/\/$/, '')
          : '';
      const cartUrl = cartBase ? `${cartBase}.js` : '/cart.js';
      const cartRes = await fetch(cartUrl, { signal });
      if (!this.isConnected) return;
      if (!cartRes.ok) {
        this.#setEmpty({ preserveIfRendered: true });
        return;
      }

      /** @type {{ items?: { product_id: number }[] }} */
      const cart = await cartRes.json();
      if (!this.isConnected) return;
      const items = cart.items ?? [];
      if (items.length === 0) {
        if (this.#scheduleTransientRetry()) return;
        this.#setEmpty();
        return;
      }

      const lastItem = items[items.length - 1];
      const productId = lastItem?.product_id;
      if (!productId) {
        if (this.#scheduleTransientRetry()) return;
        this.#setEmpty({ preserveIfRendered: true });
        return;
      }

      this.#transientRefreshRetryCount = 0;

      /** @type {Set<number>} */
      const cartProductIds = new Set();
      for (const item of items) {
        const raw = item?.product_id;
        const id = typeof raw === 'number' ? raw : Number(raw);
        if (Number.isFinite(id)) cartProductIds.add(id);
      }

      const rawRecommendations = await this.#fetchRecommendations(productId, signal);
      if (!this.isConnected) return;
      const limit = Number.parseInt(String(this.dataset.limit ?? '8'), 10) || 8;

      let products = this.#filterOutCartProducts(
        rawRecommendations
          .map((p) => this.#normalizeRecommendationProduct(p))
          .filter((p) => p !== null),
        cartProductIds
      );

      if (products.length < limit) {
        const seen = new Set(products.map((p) => p.id));
        const fillers = this.#takeFromCatalogFallback(cartProductIds, seen, limit - products.length);
        products = [...products, ...fillers];
      }

      if (products.length === 0) {
        products = this.#takeFromCatalogFallback(cartProductIds, new Set(), limit);
      }

      products = products.slice(0, limit);
      if (!this.isConnected) return;
      await this.#renderCarousel(products, signal, cartProductIds, true);
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
      console.warn('[cart-product-recommendations]', 'refresh failed', e);
      if (this.#scheduleTransientRetry()) return;
      if (this.isConnected) this.#setEmpty({ preserveIfRendered: true });
    } finally {
      if (this.isConnected) this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {number} productId
   * @param {AbortSignal} signal
   * @returns {Promise<RecommendationProduct[]>}
   */
  async #fetchRecommendations(productId, signal) {
    const base = (this.dataset.recommendationsBaseUrl ?? '').replace(/\/$/, '');
    const limit = this.dataset.limit ?? '8';
    const intent = this.dataset.intent ?? '';

    const buildUrl = (/** @type {boolean} */ withIntent) => {
      const params = new URLSearchParams({
        product_id: String(productId),
        limit: String(limit),
      });
      if (withIntent && intent) params.set('intent', intent);
      return `${base}.json?${params.toString()}`;
    };

    const tryFetch = async (/** @type {boolean} */ withIntent) => {
      const url = buildUrl(withIntent);
      const cached = this.#recommendationsCache[url];
      if (cached) return /** @type {RecommendationProduct[]} */ (cached);

      const res = await fetch(url, { signal });
      if (!res.ok) return null;

      /** @type {{ products?: RecommendationProduct[] }} */
      const data = await res.json();
      const products = data.products ?? [];
      this.#recommendationsCache[url] = products;
      return products;
    };

    let products = await tryFetch(true);
    if (products === null && intent) {
      products = await tryFetch(false);
    }
    return products ?? [];
  }

  /**
   * @param {RecommendationProduct[]} products
   * @param {Set<number>} cartProductIds
   * @returns {RecommendationProduct[]}
   */
  #filterOutCartProducts(products, cartProductIds) {
    return products.filter((p) => {
      if (!p) return false;
      const id = typeof p.id === 'number' ? p.id : Number(p.id);
      if (!Number.isFinite(id) || cartProductIds.has(id)) return false;
      const handle = typeof p.handle === 'string' ? p.handle.trim() : '';
      const url = typeof p.url === 'string' ? p.url.trim() : '';
      const path = this.#productPathFromUrl(url);
      return Boolean(handle || path);
    });
  }

  /**
   * Coerce recommendation payload into a shape we can fetch (handle / relative product path).
   * @param {unknown} raw
   * @returns {RecommendationProduct | null}
   */
  #normalizeRecommendationProduct(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const p = /** @type {Record<string, unknown>} */ (raw);
    const id = typeof p.id === 'number' ? p.id : Number(p.id);
    if (!Number.isFinite(id)) return null;

    let handle = typeof p.handle === 'string' ? p.handle.trim() : '';
    let url = typeof p.url === 'string' ? p.url.trim() : '';

    if (url && /^https?:\/\//i.test(url)) {
      try {
        const u = new URL(url);
        url = `${u.pathname}${u.search}`;
      } catch {
        /* keep */
      }
    }

    if (!handle && url) {
      const fromPath = this.#handleFromProductPath(url);
      if (fromPath) handle = fromPath;
    }

    const title = typeof p.title === 'string' ? p.title : '';
    return { id, title, handle: handle || undefined, url: url || undefined };
  }

  /**
   * @param {string} urlOrPath
   * @returns {string}
   */
  #productPathFromUrl(urlOrPath) {
    const t = urlOrPath.trim();
    if (!t) return '';
    if (t.startsWith('/')) return t.split('?')[0] ?? t;
    if (/^https?:\/\//i.test(t)) {
      try {
        return new URL(t).pathname.split('?')[0] ?? '';
      } catch {
        return '';
      }
    }
    return '';
  }

  /**
   * @param {string} urlOrPath
   * @returns {string}
   */
  #handleFromProductPath(urlOrPath) {
    const path = this.#productPathFromUrl(urlOrPath);
    const m = path.match(/\/products\/([^/?#]+)/);
    return m?.[1] ? decodeURIComponent(m[1]) : '';
  }

  /**
   * @param {Set<number>} cartProductIds
   * @param {Set<number>} skipIds
   * @param {number} maxCount
   * @returns {RecommendationProduct[]}
   */
  #takeFromCatalogFallback(cartProductIds, skipIds, maxCount) {
    if (maxCount <= 0) return [];

    const script =
      this.refs.catalogFallback ?? this.querySelector('script[type="application/json"][ref="catalogFallback"]');
    if (!(script instanceof HTMLScriptElement)) return [];

    const raw = script.textContent?.trim();
    if (!raw) return [];

    try {
      /** @type {unknown} */
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      /** @type {RecommendationProduct[]} */
      const out = [];
      for (const entry of parsed) {
        if (!entry || typeof entry !== 'object') continue;
        const id = /** @type {{ id?: unknown }} */ (entry).id;
        const title = /** @type {{ title?: unknown }} */ (entry).title;
        const handle = /** @type {{ handle?: unknown }} */ (entry).handle;
        const url = /** @type {{ url?: unknown }} */ (entry).url;
        if (typeof id !== 'number' || cartProductIds.has(id) || skipIds.has(id)) continue;
        const h = typeof handle === 'string' ? handle.trim() : '';
        const u = typeof url === 'string' ? url.trim() : '';
        if (!h && !(u && (u.startsWith('/') || /^https?:\/\//i.test(u)))) continue;
        const titleStr = typeof title === 'string' ? title : '';
        out.push({
          id,
          title: titleStr || h || 'Product',
          handle: h || undefined,
          url: u || undefined,
        });
        skipIds.add(id);
        if (out.length >= maxCount) break;
      }
      return out;
    } catch {
      return [];
    }
  }

  /**
   * @param {RecommendationProduct} product
   * @param {string} sectionId
   * @returns {string}
   */
  #productSectionRenderUrl(product, sectionId) {
    let path = '';
    if (typeof product.url === 'string' && product.url.trim()) {
      path = this.#productPathFromUrl(product.url) || '';
    }
    if (!path) {
      const h = String(product.handle ?? '').trim();
      if (h) path = `/products/${encodeURIComponent(h)}`;
    }
    if (!path) return '';
    const url = new URL(path, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  /**
   * @param {RecommendationProduct} product
   * @param {string} sectionId
   * @param {AbortSignal} signal
   * @returns {Promise<string>}
   */
  async #fetchProductCardMarkup(product, sectionId, signal) {
    const requestUrl = this.#productSectionRenderUrl(product, sectionId);
    if (!requestUrl) return '';
    const cacheKey = requestUrl;
    const cached = this.#cardMarkupCache.get(cacheKey);
    if (cached) return cached;

    const res = await fetch(requestUrl, { signal });
    if (!res.ok) return '';

    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const inner = this.#extractCardMarkupFromSectionDoc(doc);
    if (inner) this.#cardMarkupCache.set(cacheKey, inner);
    return inner;
  }

  /**
   * @param {Document} doc
   * @returns {string}
   */
  #extractCardMarkupFromSectionDoc(doc) {
    let mount = doc.querySelector('[data-cart-drawer-recommendation-card]');
    if (mount?.innerHTML?.trim()) return mount.innerHTML.trim();

    mount = doc.querySelector('.cart-drawer__recommendation-card-item');
    if (mount?.innerHTML?.trim()) return mount.innerHTML.trim();

    const sectionRoot = doc.querySelector('[id^="shopify-section-"]');
    const productCard = sectionRoot?.querySelector('product-card');
    if (productCard) {
      return `<div class="resource-list__item cart-drawer__recommendation-card-item" data-cart-drawer-recommendation-card>${productCard.outerHTML}</div>`;
    }

    return '';
  }

  /**
   * @param {RecommendationProduct[]} products
   * @param {AbortSignal} signal
   * @param {Set<number>} cartProductIds
   * @param {boolean} [allowCatalogRetry]
   */
  async #renderCarousel(products, signal, cartProductIds, allowCatalogRetry = false) {
    const mount = this.#mountEl();
    const status = this.refs.status ?? this.querySelector('[ref="status"]');
    if (!mount) return;

    if (products.length === 0) {
      this.#setEmpty({ preserveIfRendered: true });
      return;
    }

    const sectionId = (this.dataset.productCardSectionId ?? 'mini-cart-recommendation').trim();
    const limit = Number.parseInt(String(this.dataset.limit ?? '8'), 10) || 8;

    const slidesPayload = await Promise.all(
      products.map(async (product, i) => {
        const normalized = this.#normalizeRecommendationProduct(product);
        if (!normalized) return { i, slideIdAttr: `idx-${i}`, markup: '' };
        const id = normalized.id;
        const slideIdAttr = Number.isFinite(id) ? String(id) : `idx-${i}`;
        const markup = await this.#fetchProductCardMarkup(normalized, sectionId, signal);
        return { i, slideIdAttr, markup };
      })
    );

    if (!this.isConnected) return;

    let slidesOrdered = slidesPayload.filter((s) => s.markup).sort((a, b) => a.i - b.i);

    if (slidesOrdered.length === 0 && allowCatalogRetry) {
      const triedIds = new Set(products.map((p) => p.id));
      const fallback = this.#takeFromCatalogFallback(cartProductIds, triedIds, limit);
      const fallbackNorm = fallback
        .map((p) => this.#normalizeRecommendationProduct(p))
        .filter((p) => p !== null);
      if (fallbackNorm.length) {
        await this.#renderCarousel(fallbackNorm, signal, cartProductIds, false);
        return;
      }
    }

    if (slidesOrdered.length === 0) {
      this.#setEmpty({ preserveIfRendered: true });
      return;
    }

    const tpl =
      this.refs.slideshowArrowsTpl ?? this.querySelector('template[ref="slideshowArrowsTpl"]');
    const arrowsHtml = tpl instanceof HTMLTemplateElement ? tpl.innerHTML.trim() : '';

    const n = slidesOrdered.length;
    const timelineScope = Array.from({ length: n }, (_, i) => `--slide-${i}`).join(', ');
    const slidesHtml = slidesOrdered
      .map(
        (s, i) =>
          `<slideshow-slide ref="slides[]" aria-hidden="${i === 0 ? 'false' : 'true'}" class="resource-list__slide" slide-id="${s.slideIdAttr.replace(/[^a-zA-Z0-9_-]/g, '')}">${s.markup}</slideshow-slide>`
      )
      .join('');

    mount.innerHTML = `
<div class="resource-list__carousel" style="--gutter-slide-width: 0px; --slide-width-max: 300px;">
  <slideshow-component
    class="resource-list__carousel"
    style="--slideshow-timeline: ${timelineScope};--slide-min-height: auto;--slide-min-height-desktop: auto;"
    initial-slide="0"
  >
    <slideshow-container ref="slideshowContainer">
      ${arrowsHtml}
      <slideshow-slides tabindex="-1" ref="scroller">${slidesHtml}</slideshow-slides>
    </slideshow-container>
  </slideshow-component>
</div>`;

    if (status) {
      status.textContent = '';
    }
    this.classList.remove('hidden');
  }

  #hasRenderedSlides() {
    const mount = this.#mountEl();
    if (!mount) return false;
    return mount.querySelector('slideshow-slide') !== null;
  }

  /**
   * @param {{ preserveIfRendered?: boolean }} [options]
   */
  #setEmpty(options = {}) {
    const { preserveIfRendered = false } = options;
    if (preserveIfRendered && this.#hasRenderedSlides()) return;

    const mount = this.#mountEl();
    const status = this.refs.status ?? this.querySelector('[ref="status"]');
    if (mount) mount.innerHTML = '';
    if (status) status.textContent = '';
    this.classList.add('hidden');
  }
}

if (!customElements.get('cart-product-recommendations')) {
  customElements.define('cart-product-recommendations', CartProductRecommendations);
}
